/**
 * Relationships Tab
 *
 * PE-775: Displays and manages object-level relationships for a specific object.
 * Shows relationships where this object is the source (has lookup fields) or
 * target (is being referenced by other objects).
 *
 * @component
 */

import React, { useState } from 'react';
import {
  Plus,
  Search,
  Link2,
  ArrowRight,
  ArrowLeft,
  Edit2,
  Trash2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Lock
} from 'lucide-react';
import {
  useObjectRelationships,
  getRelationshipTypeLabel,
  getRelationshipTypeColor,
  useDeleteRelationship,
  type ObjectRelationship
} from '../../../services/queries/objectRelationshipQueries';
import type { CustomObject } from '../../../services/queries/objectQueries';
import { DevModeBanner } from '../../common/DevModeBanner';
import { CreateRelationshipModal } from './CreateRelationshipModal';
import { EditRelationshipModal } from './EditRelationshipModal';
import { useToast } from '../../../contexts/ToastContext';

interface RelationshipsTabProps {
  objectId: string;
  object: CustomObject;
}

export const RelationshipsTab: React.FC<RelationshipsTabProps> = ({ objectId, object }) => {
  const { success, error: showError } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [directionFilter, setDirectionFilter] = useState<'all' | 'outgoing' | 'incoming'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRelationship, setEditingRelationship] = useState<ObjectRelationship | null>(null);
  const [expandedRelationship, setExpandedRelationship] = useState<string | null>(null);
  const [relToDelete, setRelToDelete] = useState<ObjectRelationship | null>(null);

  const { data: relationships, isLoading, error } = useObjectRelationships(objectId, {
    direction: 'both',
    includeInactive: false
  });

  const deleteRelationship = useDeleteRelationship();

  // Filter relationships
  const filteredRelationships = relationships?.filter(rel => {
    // Search filter
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      const matchesSearch =
        rel.fieldLabel.toLowerCase().includes(search) ||
        rel.fieldName.toLowerCase().includes(search) ||
        rel.targetObjectLabel.toLowerCase().includes(search) ||
        rel.sourceObjectLabel.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }

    // Direction filter
    if (directionFilter === 'outgoing' && rel.sourceObjectId !== objectId) return false;
    if (directionFilter === 'incoming' && rel.targetObjectId !== objectId) return false;

    return true;
  }) || [];

  // Separate by direction
  const outgoingRelationships = filteredRelationships.filter(r => r.sourceObjectId === objectId);
  const incomingRelationships = filteredRelationships.filter(r => r.targetObjectId === objectId);

  const openDeleteConfirm = (rel: ObjectRelationship) => {
    if (rel.isStandard) {
      showError('Cannot Delete', 'Standard relationships cannot be deleted.');
      return;
    }
    setRelToDelete(rel);
  };

  const closeDeleteConfirm = () => setRelToDelete(null);

  const confirmDelete = async () => {
    if (!relToDelete || deleteRelationship.isPending) return;
    const rel = relToDelete;
    setRelToDelete(null);
    try {
      await deleteRelationship.mutateAsync({ relationshipId: rel.id, relationship: rel });
      success('Relationship Deleted', `"${rel.fieldLabel}" has been deleted.`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete relationship. Please try again.';
      showError('Error', msg);
    }
  };

  const toggleExpand = (relId: string) => {
    setExpandedRelationship(prev => prev === relId ? null : relId);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Relationships</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Define how {object.label} connects to other objects
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <Plus className="w-4 h-4" />
            New Relationship
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search relationships..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-gray-400" />
            <select
              value={directionFilter}
              onChange={(e) => setDirectionFilter(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Directions</option>
              <option value="outgoing">Outgoing (Lookups from {object.label})</option>
              <option value="incoming">Incoming (Referenced by other objects)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Development Mode Banner */}
      <DevModeBanner
        dataSource="mockDataManager.ts"
        itemCount={`${relationships?.length || 0} relationships`}
        customMessage={`Development Mode: Showing ${filteredRelationships.length} relationships for ${object.label}. Data persisted via mockDataManager.ts (localStorage).`}
        dismissible={true}
      />

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading relationships...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-900 dark:text-red-100">Error loading relationships</p>
            <p className="text-sm text-red-800 dark:text-red-300 mt-1">Please try again or contact support.</p>
          </div>
        </div>
      )}

      {/* Relationships List */}
      {!isLoading && !error && (
        <>
          {/* Results Count */}
          <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            Showing {filteredRelationships.length} relationship{filteredRelationships.length !== 1 ? 's' : ''}
            {' '}({outgoingRelationships.length} outgoing, {incomingRelationships.length} incoming)
          </div>

          {/* Outgoing Relationships (Lookups from this object) */}
          {(directionFilter === 'all' || directionFilter === 'outgoing') && outgoingRelationships.length > 0 && (
            <div className="mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                  <ArrowRight className="w-4 h-4 text-blue-500" />
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                    Outgoing Relationships
                  </h3>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    (Lookup/Master-Detail fields on {object.label})
                  </span>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {outgoingRelationships.map((rel) => (
                    <RelationshipRow
                      key={rel.id}
                      relationship={rel}
                      currentObjectId={objectId}
                      isExpanded={expandedRelationship === rel.id}
                      onToggleExpand={() => toggleExpand(rel.id)}
                      onEdit={() => setEditingRelationship(rel)}
                      onDelete={() => openDeleteConfirm(rel)}
                      deletePending={deleteRelationship.isPending}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Incoming Relationships (References to this object) */}
          {(directionFilter === 'all' || directionFilter === 'incoming') && incomingRelationships.length > 0 && (
            <div className="mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4 text-green-500" />
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                    Incoming Relationships
                  </h3>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    (Other objects that reference {object.label})
                  </span>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {incomingRelationships.map((rel) => (
                    <RelationshipRow
                      key={rel.id}
                      relationship={rel}
                      currentObjectId={objectId}
                      isExpanded={expandedRelationship === rel.id}
                      onToggleExpand={() => toggleExpand(rel.id)}
                      onEdit={() => setEditingRelationship(rel)}
                      onDelete={() => openDeleteConfirm(rel)}
                      deletePending={deleteRelationship.isPending}
                      isIncoming
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Empty State */}
          {filteredRelationships.length === 0 && (
            <div className="text-center py-12">
              <Link2 className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                No relationships found
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {searchQuery || directionFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria'
                  : `Create your first relationship to connect ${object.label} to other objects`}
              </p>
              {(searchQuery || directionFilter !== 'all') ? (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setDirectionFilter('all');
                  }}
                  className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium focus:outline-none focus:underline"
                >
                  Clear filters
                </button>
              ) : (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Create First Relationship
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* Create Relationship Modal */}
      <CreateRelationshipModal
        sourceObjectId={objectId}
        sourceObjectName={object.apiName}
        sourceObjectLabel={object.label}
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      {/* Edit Relationship Modal */}
      <EditRelationshipModal
        relationship={editingRelationship}
        isOpen={!!editingRelationship}
        onClose={() => setEditingRelationship(null)}
      />

      {/* Delete confirm modal */}
      {relToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="delete-confirm-title">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 id="delete-confirm-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Delete relationship?</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Are you sure you want to delete the &quot;{relToDelete.fieldLabel}&quot; relationship? This cannot be undone.
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeDeleteConfirm}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmDelete}
                    disabled={deleteRelationship.isPending}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                  >
                    {deleteRelationship.isPending ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// RELATIONSHIP ROW COMPONENT
// ============================================

interface RelationshipRowProps {
  relationship: ObjectRelationship;
  currentObjectId: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
  deletePending?: boolean;
  isIncoming?: boolean;
}

const RelationshipRow: React.FC<RelationshipRowProps> = ({
  relationship,
  currentObjectId,
  isExpanded,
  onToggleExpand,
  onEdit,
  onDelete,
  deletePending = false,
  isIncoming = false
}) => {
  const rel = relationship;

  return (
    <div className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
      {/* Main Row */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {/* Relationship Name */}
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {rel.fieldLabel}
              </span>

              {/* Type Badge */}
              <span className={`px-2 py-0.5 text-xs rounded font-medium ${getRelationshipTypeColor(rel.relationshipType)}`}>
                {getRelationshipTypeLabel(rel.relationshipType)}
              </span>

              {/* Required Badge */}
              {rel.isRequired && (
                <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded font-medium">
                  Required
                </span>
              )}

              {/* Standard Badge */}
              {rel.isStandard && (
                <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded font-medium flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Standard
                </span>
              )}
            </div>

            {/* Relationship Path */}
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              {isIncoming ? (
                <>
                  <span className="font-mono">{rel.sourceObjectLabel}</span>
                  <ArrowRight className="w-4 h-4" />
                  <span className="font-semibold text-gray-900 dark:text-gray-100">{rel.targetObjectLabel}</span>
                </>
              ) : (
                <>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">{rel.sourceObjectLabel}</span>
                  <ArrowRight className="w-4 h-4" />
                  <span className="font-mono">{rel.targetObjectLabel}</span>
                </>
              )}
              <span>•</span>
              <span className="font-mono text-xs">{rel.fieldName}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="ml-4 flex items-center gap-2">
            <button
              onClick={onToggleExpand}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Show details"
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            <button
              onClick={onEdit}
              className="flex items-center gap-2 px-3 py-1.5 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <Edit2 className="w-4 h-4" />
              <span className="text-sm font-medium">Edit</span>
            </button>
            {!rel.isStandard && (
              <button
                type="button"
                disabled={deletePending}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete();
                }}
                className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 rounded-lg disabled:opacity-50 disabled:pointer-events-none"
                title="Delete relationship"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-4 pb-4">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400 block mb-1">Child Relationship Name</span>
              <span className="font-mono text-gray-900 dark:text-gray-100">{rel.childRelationshipName}</span>
            </div>
            {rel.relationshipType === 'master-detail' && (
              <>
                <div>
                  <span className="text-gray-500 dark:text-gray-400 block mb-1">Cascade Delete</span>
                  <span className={rel.cascadeDelete ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-300'}>
                    {rel.cascadeDelete ? 'Yes' : 'No'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400 block mb-1">Share Access</span>
                  <span className="text-gray-900 dark:text-gray-100">{rel.shareAccessLevel}</span>
                </div>
              </>
            )}
            {rel.description && (
              <div className="col-span-2 md:col-span-4">
                <span className="text-gray-500 dark:text-gray-400 block mb-1">Description</span>
                <span className="text-gray-900 dark:text-gray-100">{rel.description}</span>
              </div>
            )}
            {rel.lookupFilter && (
              <div className="col-span-2 md:col-span-4">
                <span className="text-gray-500 dark:text-gray-400 block mb-1">Lookup Filter</span>
                <span className="font-mono text-xs text-gray-900 dark:text-gray-100">{rel.lookupFilter}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RelationshipsTab;
